import { useCallback, useMemo } from 'react'
import { Check, KeyRound, ScanSearch } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchLeaked, resolveLeaked, DEV_STR as S, type LeakedTokenRow } from './devApi'

/**
 * A13.08 — Утёкшие токены (GitHub/GitLab secret scanner). Open findings can be
 * revoked (kills the token) or ignored (false positive). Dormant until
 * `/admin/developers/leaked` ships. Placeholder permission USERS_VIEW.
 */
export function LeakedTokensScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<LeakedTokenRow>[]>(
    () => [
      { key: 'token_masked', header: S.name, type: 'text' },
      { key: 'kind', header: S.kind, type: 'text' },
      { key: 'source', header: S.source, type: 'text' },
      { key: 'found_at', header: S.foundAt, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: LeakedTokenRow, action: 'revoke' | 'ignore') => {
      try {
        await resolveLeaked(row.id, action)
        toast(action === 'revoke' ? S.revoked : S.ignored, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<LeakedTokenRow>[]>(
    () => [
      {
        id: 'revoke',
        label: S.revoke,
        icon: <KeyRound size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'revoke'),
      },
      {
        id: 'ignore',
        label: S.ignore,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'ignore'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchLeaked, [])
  return (
    <>
      <PageHeader title={S.leaked} screenId="A13.08" />
      <DataTable<LeakedTokenRow>
        screenId="A13.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <ScanSearch size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
